import React, { useMemo } from "react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import "./VendorWhatsAppFloat.css";

/* Exported because this is the only place in the web client that turns a
   VENDOR's saved phone number into a wa.me link, and a second surface now needs
   one: the healthcare product dialog, which has to point a shopper at the
   pharmacy for a quote-only item because there is no in-platform healthcare
   quote flow yet. One implementation, two call sites, rather than a second
   almost-right copy of the Uganda trunk-code rules. */
export function normalizeWhatsAppNumber(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits || /^0+$/.test(digits)) return "";

  if (digits.startsWith("256")) {
    return digits.length === 12 ? digits : "";
  }
  if (digits.startsWith("0")) {
    return digits.length === 10 ? `256${digits.slice(1)}` : "";
  }
  if (digits.length === 9) return `256${digits}`;

  return digits.length >= 10 && digits.length <= 15 ? digits : "";
}

function buildMessage({ vendorName, vendorType, pageUrl }) {
  const name = vendorName || "this vendor";
  const type = vendorType ? `${vendorType} ` : "";
  return [
    `Hi ${name}, I found your ${type}shop on Lusuku.`,
    "I would like to ask about an order.",
    pageUrl ? `Page: ${pageUrl}` : "",
  ].filter(Boolean).join("\n");
}

/**
 * The wa.me link for one vendor, or "" when their number is unusable.
 *
 * `message` overrides the generic "I would like to ask about an order" body, so
 * a caller with something specific to ask (a named product, a quantity) can say
 * it without rebuilding the number rules.
 */
export function buildVendorWhatsAppHref({
  phone,
  contactUrl,
  vendorName,
  vendorType,
  pageUrl,
  message,
} = {}) {
  if (contactUrl) return contactUrl;
  const number = normalizeWhatsAppNumber(phone);
  if (!number) return "";
  const text = message || buildMessage({ vendorName, vendorType, pageUrl });
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
}

export default function VendorWhatsAppFloat({
  phone,
  contactUrl,
  vendorName,
  vendorType,
  pageUrl,
}) {
  const href = useMemo(
    () =>
      buildVendorWhatsAppHref({
        phone,
        contactUrl,
        vendorName,
        vendorType,
        pageUrl,
      }),
    [phone, contactUrl, vendorName, vendorType, pageUrl]
  );

  if (!href) return null;

  return (
    <a
      className="vendor-whatsapp-float"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Chat with ${vendorName || "vendor"} on WhatsApp`}
    >
      <span className="vendor-whatsapp-float__icon">
        <WhatsAppIcon fontSize="small" />
      </span>
      <span className="vendor-whatsapp-float__text">
        <strong>WhatsApp vendor</strong>
        <small>Ask before ordering</small>
      </span>
    </a>
  );
}
